import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text/Text'
import { DepositFreespinBonus } from 'config/types/bonus/userBonus'
import { RowBetween } from 'layout/Components/Row'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Icons } from 'svgs'
import colors from 'theme/ui/colors'
import { buildPluralizeText } from 'utils'
import {
  freeSpinBonusFiatReward,
  isFreeSpinBonusFiatReward,
  isWagerBonusFiatReward,
  wagerBonusFiatReward,
} from 'utils/bonus'
import { useFreespinGamesModal } from 'views/BonusCenter/hooks'
import BonusDetailStepCard from '../BonusDetailStepCard'

interface DepositFreespinBonusInfoSectionProps {
  bonus: DepositFreespinBonus
  step: number
  isActive: boolean
  isProcessing: boolean
}

const DepositFreespinBonusInfoSection: React.FC<DepositFreespinBonusInfoSectionProps & BoxProps> = ({
  bonus,
  step,
  isActive,
  isProcessing,
  ...props
}) => {
  const { t } = useTranslation()
  const [presentModalChooseGame] = useFreespinGamesModal(bonus)

  return (
    <Box {...props}>
      <BonusDetailStepCard
        isActive={isActive}
        isProcessing={isProcessing}
        cardTitle={
          <Flex flexDirection="column">
            <Text
              textTransform="uppercase"
              fontSize="10px"
              fontWeight="600"
              ml="8px"
              lineHeight="normal"
              color="textAlt1"
            >
              <Trans>Step {{ step }}</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" lineHeight="22px" ml="8px">
              <Trans>Spin Games</Trans>
            </Text>
          </Flex>
        }
      >
        <Box borderRadius="8px" ml="8px" mt="12px">
          <RowBetween mb="16px">
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>FS Games</Trans>
            </Text>
            <StyledTextButton alignItems="center" onClick={() => presentModalChooseGame()}>
              <Text mr="5px" fontSize="12px" color="textSubtle">
                <Trans>View list</Trans>
              </Text>
              <Icons.ChevronRightIcon width="4.5px" fill={colors.textDisable} />
            </StyledTextButton>
          </RowBetween>

          <RowBetween mb="16px">
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>Bet for one FS</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              {isFreeSpinBonusFiatReward(bonus) ? (
                <>
                  {freeSpinBonusFiatReward(bonus)?.symbol} {bonus.spinPrice}{' '}
                </>
              ) : isWagerBonusFiatReward(bonus) ? (
                <>
                  {wagerBonusFiatReward(bonus)?.symbol} {bonus.spinPrice}{' '}
                </>
              ) : (
                `$${bonus.spinPrice}`
              )}
            </Text>
          </RowBetween>

          <RowBetween>
            <Flex>
              <Text fontSize="14px" fontWeight="300" color="textSubtle">
                <Trans>Spin Duration</Trans>
              </Text>
              <Box
                data-tooltip-id="common-tip-bottom"
                data-tooltip-content={t('The time window complete the spin and deposit (if required)')}
                ml={2}
                height="16px"
              >
                <Icons.InfoCircleIcon fill={colors.textDisable} />
              </Box>
            </Flex>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              {t(
                buildPluralizeText('{{spinDuration}} {{day}}', [
                  { number: bonus.spinDurationInDays, key: 'day', word: ['day', 'days'] },
                ]),
                { spinDuration: bonus.spinDurationInDays },
              )}
            </Text>
          </RowBetween>
        </Box>
      </BonusDetailStepCard>
    </Box>
  )
}

const StyledTextButton = styled(Flex)`
  cursor: pointer;
  align-items: center;

  svg {
    fill: ${({ theme }) => theme.colors.textAlt1};
  }

  &:hover {
    ${Text} {
      color: ${({ theme }) => theme.colors.text} !important;
    }

    svg {
      fill: ${({ theme }) => theme.colors.text};
    }
  }
`

export default DepositFreespinBonusInfoSection
